import { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import type { Member } from '../types';

interface MemberSelectorProps {
  members: Member[];
  selectedMemberId: string;
  onSelect: (memberId: string) => void;
  error?: string;
  disabled?: boolean;
}

const MemberSelector = ({ members, selectedMemberId, onSelect, error, disabled }: MemberSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedMember = members.find(m => m.id === selectedMemberId);
  const favoriteMembers = members.filter(m => m.isFavorite);

  const filteredMembers = members.filter(member =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (memberId: string) => {
    onSelect(memberId);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-foreground mb-2">
        Select Member <span className="text-destructive">*</span>
      </label>
      
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between gap-3 px-4 py-3 rounded-md border
          transition-interactive
          ${error ? 'border-destructive' : 'border-input'}
          ${disabled ? 'bg-muted cursor-not-allowed opacity-60' : 'bg-card hover:border-ring'}
        `}
      >
        {selectedMember ? (
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Image
              src={selectedMember.avatar}
              alt={`${selectedMember.name} profile photo`}
              className="w-8 h-8 rounded-full object-cover"
            />
            <div className="text-left flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground truncate">
                {selectedMember.name}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {selectedMember.email}
              </div>
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">Choose a member...</span>
        )}
        <Icon 
          name={isOpen ? "ChevronUp" : "ChevronDown"} 
          size={20} 
          className="text-muted-foreground flex-shrink-0"
        />
      </button>

      {error && (
        <p className="mt-1 text-sm text-destructive">{error}</p>
      )}

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-popover border border-border rounded-md shadow-elevation-2 max-h-96 overflow-hidden">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Icon 
                name="Search" 
                size={18} 
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                autoFocus
              />
            </div>
          </div>

          <div className="overflow-y-auto max-h-80">
            {favoriteMembers.length > 0 && searchQuery === '' && (
              <div className="p-2 border-b border-border">
                <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Favorites
                </div>
                {favoriteMembers.map(member => (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => handleSelect(member.id)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-sm
                      transition-interactive hover:bg-muted
                      ${member.id === selectedMemberId ? 'bg-primary/10' : ''}
                    `}
                  >
                    <Image
                      src={member.avatar}
                      alt={`${member.name} profile photo`}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-sm font-medium text-popover-foreground truncate">
                        {member.name}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {member.email}
                      </div>
                    </div>
                    <Icon name="Star" size={16} color="var(--color-warning)" />
                  </button>
                ))}
              </div>
            )}

            <div className="p-2">
              {searchQuery !== '' && (
                <div className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Search Results ({filteredMembers.length})
                </div>
              )}
              {filteredMembers.length === 0 ? (
                <div className="px-3 py-8 text-center">
                  <Icon name="Users" size={32} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No members found</p>
                </div>
              ) : (
                filteredMembers.map(member => (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => handleSelect(member.id)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-sm
                      transition-interactive hover:bg-muted
                      ${member.id === selectedMemberId ? 'bg-primary/10' : ''}
                    `}
                  >
                    <Image
                      src={member.avatar}
                      alt={`${member.name} profile photo`}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-sm font-medium text-popover-foreground truncate">
                        {member.name}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {member.email} • ${member.totalContributions.toLocaleString('en-US', { minimumFractionDigits: 2 })} total
                      </div>
                    </div>
                    {member.id === selectedMemberId && (
                      <Icon name="Check" size={18} color="var(--color-primary)" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberSelector;